"use client";

/**
 * AI SDK Elements — `conversation`. Copiat del registre (no és un paquet), com `tool.tsx`:
 *   https://registry.ai-sdk.dev/conversation.json · versió del registre del 13-OCT-2026.
 * Documentació: https://elements.ai-sdk.dev/docs
 *
 * Una desviació gran, marcada amb `[Guilleumes]`: l'original és un embolcall de
 * `use-stick-to-bottom` (`StickToBottom`, `useStickToBottomContext`). Aquí no s'ha afegit la
 * dependència: el mateix comportament («si ets a baix, segueixes a baix; si has pujat a llegir,
 * no t'estiro») el fa `useStickToBottom` d'aquest fitxer amb un `scroll` i un `MutationObserver`.
 * Els noms, les classes i les props dels quatre components són els de l'original, perquè
 * substituir-lo pel de debò sigui canviar aquest fitxer i prou. Els textos, en català.
 */

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ArrowDownIcon } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/** A quants píxels del final encara es considera «a baix». */
const BOTTOM_THRESHOLD_PX = 48;

type StickToBottomContextValue = {
  isAtBottom: boolean;
  scrollToBottom: () => void;
  scrollRef: React.RefObject<HTMLDivElement | null>;
};

const StickToBottomContext = createContext<StickToBottomContextValue | null>(
  null
);

// [Guilleumes] El substitut de `useStickToBottomContext` de la llibreria.
const useStickToBottomContext = () => {
  const context = useContext(StickToBottomContext);

  if (!context) {
    throw new Error("Conversation components must be used within Conversation");
  }

  return context;
};

export type ConversationProps = ComponentProps<"div">;

export const Conversation = ({
  className,
  children,
  ...props
}: ConversationProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  // El valor que llegeix l'observador: l'estat de React arriba un render tard.
  const stickRef = useRef(true);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    stickRef.current = true;
    setIsAtBottom(true);
    el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => {
      const atBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_THRESHOLD_PX;
      stickRef.current = atBottom;
      setIsAtBottom(atBottom);
    };
    el.addEventListener("scroll", onScroll, { passive: true });

    // Contingut nou (un tros de text, una targeta): si s'era a baix, s'hi torna.
    const observer = new MutationObserver(() => {
      if (stickRef.current) el.scrollTop = el.scrollHeight;
    });
    observer.observe(el, { childList: true, subtree: true, characterData: true });
    el.scrollTop = el.scrollHeight;

    return () => {
      el.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, []);

  const value = useMemo(
    () => ({ isAtBottom, scrollToBottom, scrollRef }),
    [isAtBottom, scrollToBottom]
  );

  return (
    <StickToBottomContext.Provider value={value}>
      <div
        // [Guilleumes] L'original posa `overflow-y-hidden` aquí i el scroll el fa la llibreria
        // en un fill; aquí l'arrel és relativa i el fill de sota és qui fa scroll.
        className={cn("relative flex-1 overflow-y-hidden", className)}
        role="log"
        {...props}
      >
        <div className="size-full overflow-y-auto" ref={scrollRef}>
          {children}
        </div>
      </div>
    </StickToBottomContext.Provider>
  );
};

export type ConversationContentProps = ComponentProps<"div">;

export const ConversationContent = ({
  className,
  ...props
}: ConversationContentProps) => (
  <div className={cn("flex flex-col gap-8 p-4", className)} {...props} />
);

export type ConversationEmptyStateProps = ComponentProps<"div"> & {
  title?: string;
  description?: string;
  icon?: ReactNode;
};

export const ConversationEmptyState = ({
  className,
  // [Guilleumes] textos en català.
  title = "Encara no hi ha missatges",
  description = "Escriu per començar la conversa",
  icon,
  children,
  ...props
}: ConversationEmptyStateProps) => (
  <div
    className={cn(
      "flex size-full flex-col items-center justify-center gap-3 p-8 text-center",
      className
    )}
    {...props}
  >
    {children ?? (
      <>
        {icon && <div className="text-muted-foreground">{icon}</div>}
        <div className="space-y-1">
          <h3 className="font-medium text-sm">{title}</h3>
          {description && (
            <p className="text-muted-foreground text-sm">{description}</p>
          )}
        </div>
      </>
    )}
  </div>
);

export type ConversationScrollButtonProps = ComponentProps<typeof Button>;

export const ConversationScrollButton = ({
  className,
  ...props
}: ConversationScrollButtonProps) => {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  const handleScrollToBottom = useCallback(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  return (
    !isAtBottom && (
      <Button
        // [Guilleumes] `aria-label`: és un botó només d'icona.
        aria-label="Ves al final de la conversa"
        className={cn(
          "absolute bottom-4 left-[50%] translate-x-[-50%] rounded-full",
          className
        )}
        onClick={handleScrollToBottom}
        size="icon"
        type="button"
        variant="outline"
        {...props}
      >
        <ArrowDownIcon className="size-4" />
      </Button>
    )
  );
};
